import { debounce } from 'radash';

import {
    CELL_HEIGHT,
    CELL_WIDTH,
    DIAGRAM_DEFAULT_COLUMNS,
    DIAGRAM_DEFAULT_ROWS,
    DIAGRAM_PADDING,
    diagramHeight,
    diagramWidth,
    SCALE_MAX,
    SCALE_MIN,
} from '@diagram/diagram-dimensions';
import { ContextMenuService } from '@diagram/services/ContextMenuService';
import { DiagramService } from '@diagram/services/DiagramService';
import { canvasState } from '@diagram/state/canvas';
import { diagram } from '@diagram/state/diagram';

import { Point, Transform } from 'models';

import { LocalStorageService } from 'services/LocalStorageService';

const bound = (value: number, min: number, max: number) => {
    if (ViewportService.skipBoundsCheck) {
        return value;
    }

    return Math.max(Math.min(value, max), min);
};

const ViewportService = {
    container: null as HTMLElement | null,
    canvas: null as HTMLElement | null,

    // Placeholder until setInitialTransform runs after the diagram loads
    transform: {
        scale: 1,
        translation: {
            x: (-1 * diagramWidth(DIAGRAM_DEFAULT_COLUMNS)) / 2,
            y: (-1 * diagramHeight(DIAGRAM_DEFAULT_ROWS)) / 2,
        },
    },

    skipBoundsCheck: false,
    transition: `200ms ease-out`,

    enabled: true,

    enable: () => {
        ViewportService.enabled = true;
    },

    disable: () => {
        ViewportService.enabled = false;
    },

    enableBoundsCheck: () => {
        ViewportService.skipBoundsCheck = false;
    },

    disableBoundsCheck: () => {
        ViewportService.skipBoundsCheck = true;
    },

    transformCSS: () => {
        const { scale, translation } = ViewportService.transform;

        return `translate(${translation.x}px, ${translation.y}px) scale(${scale})`;
    },

    reverseTransformCSS: () => {
        const { scale } = ViewportService.transform;

        return `scale(calc(1 / ${scale}))`;
    },

    nonScalableStyle: (cornerAnchor = false) => {
        return {
            transform: 'var(--viewport-reverse-transform)',
            transition: `transform ${ViewportService.transition}`,
            transformOrigin: cornerAnchor ? 'top left' : undefined,
        };
    },

    setTransform: (
        nextTransform: Transform,
        options: {
            smooth?: boolean;
        } = {
            smooth: false,
        },
    ) => {
        const { canvas } = ViewportService;

        if (!canvas || !ViewportService.enabled) {
            return;
        }

        ContextMenuService.closeContextMenu();
        ViewportService.transform = ViewportService.getTransformWithinBounds(nextTransform);

        if (options.smooth) {
            canvas.style.transition = `transform ${ViewportService.transition}`;

            const removeSmoothTransition = () => {
                if (canvas) {
                    canvas.style.transition = 'none';
                    canvas.removeEventListener('transitionend', removeSmoothTransition);
                }
            };

            canvas.addEventListener('transitionend', removeSmoothTransition);

            const removeCheckIfInViewport = () => {
                DiagramService.debouncedCheckIfInViewport();

                canvas.removeEventListener('transitionend', removeCheckIfInViewport);
            };

            canvas.addEventListener('transitionend', removeCheckIfInViewport);
        }

        canvas.style.transform = ViewportService.transformCSS();
        canvas.style.setProperty('--viewport-reverse-transform', ViewportService.reverseTransformCSS());
        ViewportService.saveLocalStorageState();

        canvasState.scale = ViewportService.transform.scale;
        canvasState.translation.x = ViewportService.transform.translation.x;
        canvasState.translation.y = ViewportService.transform.translation.y;

        if (!options.smooth) {
            DiagramService.debouncedCheckIfInViewport();
        }

        return ViewportService.transform;
    },

    getContainerCenter: () => {
        const { width, height } = ViewportService.container!.getBoundingClientRect();

        return {
            x: width / 2,
            y: height / 2,
        };
    },

    // viewport coordinates to canvas coordinates
    getLocalCoordinates: (point: Point): Point => {
        if (!ViewportService.canvas) return point;

        const { scale } = ViewportService.transform;
        const { x, y } = ViewportService.canvas.getBoundingClientRect();

        return {
            x: (point.x - x) / scale - DIAGRAM_PADDING,
            y: (point.y - y) / scale - DIAGRAM_PADDING,
        };
    },

    getCanvasCenterPosition: () => {
        const container = document.querySelector('#diagram-canvas-container')!;

        return {
            x: container.getBoundingClientRect().width / 2,
            y: container.getBoundingClientRect().height / 2,
        };
    },

    getCanvasCenter: () => {
        const position = ViewportService.getCanvasCenterPosition();

        return ViewportService.getLocalCoordinates(position);
    },

    getCell: (localCoordinates: Point): Point => {
        const { x, y } = localCoordinates;

        return {
            x: Math.floor(x / CELL_WIDTH),
            y: Math.floor(y / CELL_HEIGHT),
        };
    },

    getTransformWithinBounds: (transform: Transform) => {
        const { container } = ViewportService;

        if (!container) {
            return transform;
        }

        const { width: containerWidth = Infinity, height: containerHeight = Infinity } =
            container.getBoundingClientRect();

        const scale = bound(transform.scale, SCALE_MIN, SCALE_MAX);

        const { columns, rows } = DiagramService.getDimensions();
        const width = diagramWidth(columns);
        const height = diagramHeight(rows);

        const X_MIN = containerWidth - (width / 2) * (1 + scale);
        const X_MAX = (-width / 2) * (1 - scale);

        const Y_MIN = containerHeight - (height / 2) * (1 + scale);
        const Y_MAX = (-height / 2) * (1 - scale);

        return {
            scale,
            translation: {
                x: bound(transform.translation.x, X_MIN, X_MAX),
                y: bound(transform.translation.y, Y_MIN, Y_MAX),
            },
        };
    },

    getLocalStorageKey: () => {
        return `diagram-viewfinder-${diagram.id || 'local'}-v2`;
    },

    saveLocalStorageState: debounce(
        {
            delay: 1000,
        },
        () => {
            LocalStorageService.store(ViewportService.getLocalStorageKey(), ViewportService.transform);
        },
    ),

    setInitialTransform: () => {
        const transform = LocalStorageService.get(ViewportService.getLocalStorageKey());

        if (transform) {
            return ViewportService.setTransform(transform);
        }

        const { componentInstances } = DiagramService.get();
        const componentInstancesAsArray = Object.values(componentInstances);

        if (componentInstancesAsArray.length === 0) {
            return ViewportService.setTransform({
                scale: 1,
                translation: {
                    x: -1 * DIAGRAM_PADDING + CELL_HEIGHT,
                    y: -1 * DIAGRAM_PADDING + CELL_HEIGHT,
                },
            });
        }

        const xxx: number[] = [];
        const yyy: number[] = [];

        componentInstancesAsArray.forEach((componentInstance) => {
            xxx.push(componentInstance.position.x);
            yyy.push(componentInstance.position.y);
        });

        const x = xxx.length ? Math.min(...xxx) : 0;
        const y = yyy.length ? Math.min(...yyy) : 0;

        return ViewportService.setTransform({
            scale: 1,
            translation: {
                x: -x * CELL_WIDTH,
                y: -y * CELL_HEIGHT,
            },
        });
    },

    // origin is in viewport (client) coordinates; defaults to the container center
    getZoomTranslation: (newScale: number, origin?: Point) => {
        const {
            transform: { translation, scale },
            container,
            canvas,
        } = ViewportService;

        if (!ViewportService.skipBoundsCheck && (newScale < SCALE_MIN || newScale > SCALE_MAX)) {
            return translation;
        }

        const scaleMovement = newScale / scale;

        const { width: containerX, height: containerY } = container!.getBoundingClientRect();
        const { width, height, x, y } = canvas!.getBoundingClientRect();

        const tx = (origin?.x ?? containerX / 2) - (x + width / 2);
        const ty = (origin?.y ?? containerY / 2) - (y + height / 2);

        return {
            x: translation.x - (scaleMovement - 1) * tx,
            y: translation.y - (scaleMovement - 1) * ty,
        };
    },

    zoom: (scale: number) => {
        return ViewportService.setTransform(
            {
                scale,
                translation: ViewportService.getZoomTranslation(scale),
            },
            {
                smooth: true,
            },
        );
    },

    // zoom towards a viewport (client) point, without the smooth transition
    // so continuous wheel/pinch input doesn't lag behind
    zoomAt: (scale: number, origin: Point) => {
        const boundedScale = bound(scale, SCALE_MIN, SCALE_MAX);

        if (boundedScale === ViewportService.transform.scale) {
            return;
        }

        return ViewportService.setTransform({
            scale: boundedScale,
            translation: ViewportService.getZoomTranslation(boundedScale, origin),
        });
    },

    zoomIn: (velocity = 0.25) => {
        const newScale = ViewportService.transform.scale + velocity;

        ViewportService.zoom(newScale);
    },

    zoomOut: (velocity = 0.25) => {
        const newScale = ViewportService.transform.scale - velocity;

        return ViewportService.zoom(newScale);
    },

    resetZoom: () => {
        return ViewportService.zoom(1);
    },

    pan: (
        position: {
            x: number;
            y: number;
        },
        options: {
            smooth?: boolean;
        } = {
            smooth: false,
        },
    ) => {
        const { transform } = ViewportService;

        return ViewportService.setTransform(
            {
                ...transform,
                translation: {
                    x: position.x,
                    y: position.y,
                },
            },
            options,
        );
    },

    moveIntoView: (
        position: {
            x: number;
            y: number;
        },
        offset: {
            x: number;
            y: number;
        } = {
            x: 0,
            y: 0,
        },
    ) => {
        const { container, transform } = ViewportService;
        const { width, height } = container!.getBoundingClientRect();

        const { columns, rows } = DiagramService.getDimensions();

        const scaleOffset = {
            x: (columns * CELL_WIDTH * (transform.scale - 1)) / 2,
            y: (rows * CELL_HEIGHT * (transform.scale - 1)) / 2,
        };

        ViewportService.pan(
            {
                x: scaleOffset.x - DIAGRAM_PADDING - (position.x + offset.x) * transform.scale + width / 2,
                y: scaleOffset.y - DIAGRAM_PADDING - (position.y + offset.y) * transform.scale + height / 2,
            },
            {
                smooth: true,
            },
        );
    },

    // Place the top-left corner of the grid at the given container offset, unscaled (used for printing)
    alignToOrigin: (offset: Point = { x: 0, y: 0 }) => {
        ViewportService.disableBoundsCheck();

        ViewportService.setTransform({
            scale: 1,
            translation: {
                x: offset.x - DIAGRAM_PADDING,
                y: offset.y - DIAGRAM_PADDING,
            },
        });

        ViewportService.enableBoundsCheck();
    },
};

export { ViewportService };
